import { useMutation, useQuery } from '@tanstack/react-query';
import type { MutationFunction, QueryFunction, QueryKey, UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset } from './models';

export const getApiEnterpriseInTuneAnalyticsOffersOfferId = (
  offerId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset>> => {
  return axios.get(`/api/Enterprise/InTuneAnalytics/Offers/${offerId}`, options);
};

export const getGetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryKey = (offerId: string) => {
  return [`/api/Enterprise/InTuneAnalytics/Offers/${offerId}`] as const;
};

export const getGetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>,
  TError = AxiosError<void>,
>(
  offerId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>, TError, TData>>; axios?: AxiosRequestConfig },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryKey(offerId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>> = ({ signal }) =>
    getApiEnterpriseInTuneAnalyticsOffersOfferId(offerId, { signal, ...axiosOptions });

  return { queryKey, queryFn, enabled: !!offerId, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>>;
export type GetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryError = AxiosError<void>;

export const useGetApiEnterpriseInTuneAnalyticsOffersOfferId = <TData = Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>, TError = AxiosError<void>>(
  offerId: string,
  options?: { query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiEnterpriseInTuneAnalyticsOffersOfferId>>, TError, TData>>; axios?: AxiosRequestConfig },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetApiEnterpriseInTuneAnalyticsOffersOfferIdQueryOptions(offerId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const putApiEnterpriseInTuneAnalyticsOffersOfferId = (
  offerId: string,
  brokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset>> => {
  return axios.put(`/api/Enterprise/InTuneAnalytics/Offers/${offerId}`, brokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset, options);
};

export const getPutApiEnterpriseInTuneAnalyticsOffersOfferIdMutationOptions = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>,
    TError,
    { offerId: string; data: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>,
  TError,
  { offerId: string; data: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset },
  TContext
> => {
  const { mutation: mutationOptions, axios: axiosOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>,
    { offerId: string; data: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset }
  > = (props) => {
    const { offerId, data } = props ?? {};

    return putApiEnterpriseInTuneAnalyticsOffersOfferId(offerId, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiEnterpriseInTuneAnalyticsOffersOfferIdMutationResult = NonNullable<Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>>;
export type PutApiEnterpriseInTuneAnalyticsOffersOfferIdMutationBody = BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset;
export type PutApiEnterpriseInTuneAnalyticsOffersOfferIdMutationError = AxiosError<void>;

export const usePutApiEnterpriseInTuneAnalyticsOffersOfferId = <TError = AxiosError<void>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>,
    TError,
    { offerId: string; data: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof putApiEnterpriseInTuneAnalyticsOffersOfferId>>,
  TError,
  { offerId: string; data: BrokerNerdsBarkerCoreModelsEnterpriseInTuneAnalyticsOfferRuleset },
  TContext
> => {
  const mutationOptions = getPutApiEnterpriseInTuneAnalyticsOffersOfferIdMutationOptions(options);

  return useMutation(mutationOptions);
};
